import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  buildProductosMeseroTree,
  mergeFamiliasGlobales,
  MESERO_KEY_GENERAL,
  normalizeProductosMeseroRow,
  ordenMeserosFormToGroupOrder,
  PRODUCTOS_MESERO_TITULO_GLOBAL,
  sortMergedFamiliasGlobales,
} from './productosMeseroReportModel';
import { encabezadoTieneDatos } from './reporteEncabezadoApi';
import { drawEncabezadoReportePdf } from './reporteEncabezadoPdf';
import { flattenNestedEncabezadoApiShape } from './popularidadReportModel';
import { isoADisplayDDMMAAAA } from './fechaReporte';

const formatFechaDMY = (iso) => (iso ? isoADisplayDDMMAAAA(String(iso).split('T')[0]) : '—');

const fmt = (n, maxFrac = 2) =>
  n == null || Number.isNaN(n) ? '—' : Number(n).toLocaleString('es-MX', { maximumFractionDigits: maxFrac });

const fmtMoney = (n) =>
  n == null || Number.isNaN(n)
    ? '—'
    : new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);

function sumApartadoTotalsFromFamilias(familias) {
  const t = { cantidad: 0, totalCosto: 0, importe: 0, utilidad: 0 };
  for (const f of familias) {
    t.cantidad += f.totals.cantidad || 0;
    t.totalCosto += f.totals.totalCosto || 0;
    t.importe += f.totals.importe || 0;
    t.utilidad += f.totals.utilidad || 0;
  }
  return t;
}

function pdfEnsureVerticalSpace(doc, y, margin, minBottomReserve) {
  const ph = doc.internal.pageSize.getHeight();
  if (y + minBottomReserve > ph - 20) {
    doc.addPage();
    return margin;
  }
  return y;
}

/** Métricas en una sola línea (compacto) para filas horizontales en PDF. */
function pdfMetricsOneLine(totals) {
  const util =
    totals.utilidad != null && !Number.isNaN(totals.utilidad)
      ? fmtMoney(totals.utilidad)
      : '—';
  return `Cant.: ${fmt(totals.cantidad)} u. · Imp.: ${fmtMoney(totals.importe)} · Util.: ${util}`;
}

/**
 * Una sola fila: texto izquierda + métricas alineadas a la derecha.
 * Reduce ligeramente el tamaño si no cabe.
 */
function pdfDrawHorizontalSummaryRow(
  doc,
  x,
  y,
  w,
  boxH,
  leftText,
  metricsText,
  {
    accentW = 0,
    inner = 2.4,
    titleFontStart = 8,
    metricsFontStart = 7.6,
    minTitle = 6.5,
    minMetrics = 6.3,
  }
) {
  const gap = 3.2;
  const contentW = w - accentW - inner * 2;
  let tSize = titleFontStart;
  let mSize = metricsFontStart;
  const fit = () => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(tSize);
    const lw0 = doc.getTextWidth(leftText);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(mSize);
    const mw0 = doc.getTextWidth(metricsText);
    return lw0 + gap + mw0 <= contentW;
  };
  while (!fit() && (tSize > minTitle || mSize > minMetrics)) {
    if (tSize > minTitle) tSize -= 0.25;
    if (mSize > minMetrics) mSize -= 0.25;
  }

  const midPt = Math.max(tSize, mSize);
  const ptToMm = 25.4 / 72;
  const baseline = y + boxH / 2 + midPt * ptToMm * 0.36;

  const lx = x + accentW + inner;
  const rx = x + w - inner;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(tSize);
  doc.setTextColor(51, 65, 85);
  doc.text(leftText, lx, baseline);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(mSize);
  doc.setTextColor(71, 85, 105);
  doc.text(metricsText, rx, baseline, { align: 'right' });
  doc.setTextColor(0);
}

/** Total agregado por sección (bebidas / alimentos / otros) — una sola fila. */
function drawPdfSectionTotalBar(doc, pageW, margin, y0, sectionLabel, totals) {
  const boxH = 7.8;
  let y = pdfEnsureVerticalSpace(doc, y0, margin, boxH + 4);
  const w = pageW - margin * 2;
  const x = margin;
  const inner = 2.4;

  doc.setFillColor(252, 252, 253);
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.22);
  doc.roundedRect(x, y, w, boxH, 1.2, 1.2, 'FD');

  const titleLine = `Total · ${sectionLabel}`;
  const metricsText = pdfMetricsOneLine(totals);
  pdfDrawHorizontalSummaryRow(doc, x, y, w, boxH, titleLine, metricsText, {
    accentW: 0,
    inner,
  });

  return y + boxH + 6;
}

/** Total mesero / total reporte — una sola fila + acento izquierdo. */
function drawPdfMeseroSummaryCard(
  doc,
  pageW,
  margin,
  y0,
  soloPorFamilia,
  meseroLabel,
  totals,
  opts
) {
  const accentW = 2;
  const boxH = 8.2;
  let y = pdfEnsureVerticalSpace(doc, y0, margin, boxH + 4);
  const w = pageW - margin * 2;
  const x = margin;
  const inner = 2.4;

  const variantGeneral = opts?.variant === 'general';
  if (variantGeneral) {
    doc.setFillColor(239, 246, 255);
    doc.setDrawColor(147, 197, 253);
    doc.setLineWidth(0.26);
    doc.roundedRect(x, y, w, boxH, 1.6, 1.6, 'FD');
    doc.setFillColor(29, 78, 216);
  } else {
    doc.setFillColor(240, 253, 250);
    doc.setDrawColor(167, 243, 208);
    doc.setLineWidth(0.26);
    doc.roundedRect(x, y, w, boxH, 1.6, 1.6, 'FD');
    doc.setFillColor(5, 150, 105);
  }
  doc.rect(x, y, accentW, boxH, 'F');

  const title =
    opts?.title ?? (soloPorFamilia ? 'Total reporte' : 'Total mesero');
  const sub =
    opts?.sub !== undefined
      ? opts.sub
      : soloPorFamilia
        ? 'Resumen del periodo'
        : String(meseroLabel || '');
  const leftText = sub ? `${title.toUpperCase()} · ${sub}` : title.toUpperCase();
  const metricsText = pdfMetricsOneLine(totals);

  pdfDrawHorizontalSummaryRow(doc, x, y, w, boxH, leftText, metricsText, {
    accentW,
    inner,
  });

  return y + boxH + 3;
}

/**
 * PDF alineado con `ProductosMeseroReportLayout`: fechas dd/MM/yyyy, agrupación por `familia` y mesero
 * (misma lógica que `buildProductosMeseroTree`), totales por familia y por mesero / total reporte.
 */
export function downloadProductosMeseroPdf({
  rows,
  /** Encabezado del API (misma forma que en la respuesta JSON). */
  encabezado,
  title = 'REPORTE PRODUCTOS VENDIDOS INCLUYENDO CORTESÍAS',
  /** Sin bloque empresa: una línea como la vista web. */
  empresaLine = 'Reporte operativo · SMARTNET',
  empresaNombre,
  empresaLinea2,
  empresaLinea3,
  cajaDescripcion,
  turnoDescripcion,
  fechaInicio,
  fechaFin,
  cajaInicio,
  cajaFin,
  turnoInicio,
  turnoFin,
  /** Global + familias: una fila por familia con totales, sin tabla de productos/menú. */
  globalSoloTotalesPorFamilia = false,
  mayorIngreso = false,
  mayorVenta = false,
  ordenMeseros = 'default',
  /** Detallado · Plano: tabla única de 12 columnas (misma forma que Excel). */
  noGlobalAsterisco = false,
}) {
  if (!Array.isArray(rows) || rows.length === 0) {
    return;
  }

  const tituloPdf = globalSoloTotalesPorFamilia ? PRODUCTOS_MESERO_TITULO_GLOBAL : title;

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 12;
  /** Ancho útil tablas (margen izq. +3 en cuerpo mesero); evita cellWidth 'auto' → NaN con jsPDF 4. x */
  const tableContentW = pageW - margin * 2 - 3;
  const meseroFamiliaNombreColW = Math.max(32, tableContentW - (22 + 18 + 24 + 24 + 24 + 26 + 26));
  /** Vista global 5 cols: suma fija de numéricos = ancho útil entre márgenes de página (sin +3 de sangría). */
  const globalTableInnerW = pageW - margin * 2;
  /** Anchos fijos Cant. + T.Costo + Importe + Utilidad (mm); Familia ocupa el resto hasta pageWidth − márgenes. */
  const globalNumericColsSum = 22 + 22 + 24 + 26;
  const meseroGlobalNombreColW = Math.max(36, globalTableInnerW - globalNumericColsSum);
  /** Padding uniforme cabecera/cuerpo/total global (evita “Familia” vs filas desalineadas). */
  const pdfGlobalCellPad = { top: 0.85, bottom: 0.85, left: 1.25, right: 1.25 };
  let y = margin;

  const addFooter = (pageNumber, totalPages) => {
    const ph = doc.internal.pageSize.getHeight();
    doc.setFontSize(8);
    doc.setTextColor(90);
    doc.text(
      `Generado ${new Date().toLocaleString('es-MX')}`,
      margin,
      ph - 8
    );
    doc.text(`Página ${pageNumber} de ${totalPages}`, pageW - margin, ph - 8, { align: 'right' });
    doc.setTextColor(0);
  };

  const encabezadoPdf =
    encabezado && typeof encabezado === 'object'
      ? flattenNestedEncabezadoApiShape(encabezado)
      : encabezado;

  if (encabezadoTieneDatos(encabezadoPdf)) {
    const encIn =
      globalSoloTotalesPorFamilia && typeof encabezadoPdf === 'object'
        ? { ...encabezadoPdf, reporteTitulo: tituloPdf }
        : encabezadoPdf;
    y = drawEncabezadoReportePdf(doc, encIn, pageW, margin, y, {
      fechaInicio,
      fechaFin,
      cajaDescripcion,
      turnoDescripcion,
      cajaInicio,
      cajaFin,
      turnoInicio,
      turnoFin,
    });
  } else {
    doc.setFont('helvetica', 'bold');
    if (empresaNombre) {
      doc.setFontSize(10);
      doc.text(empresaNombre, pageW / 2, y, { align: 'center' });
      y += 5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      if (empresaLinea2) {
        const lines = doc.splitTextToSize(empresaLinea2, pageW - margin * 2);
        lines.forEach((ln) => {
          doc.text(ln, pageW / 2, y, { align: 'center' });
          y += 3.5;
        });
      }
      if (empresaLinea3) {
        const lines = doc.splitTextToSize(empresaLinea3, pageW - margin * 2);
        lines.forEach((ln) => {
          doc.text(ln, pageW / 2, y, { align: 'center' });
          y += 3.5;
        });
      }
      y += 2;
      doc.setFont('helvetica', 'bold');
    } else {
      doc.setFontSize(10);
      doc.text(empresaLine, pageW / 2, y, { align: 'center' });
      y += 6;
    }
    doc.setFontSize(11);
    doc.text(tituloPdf, pageW / 2, y, { align: 'center' });
    y += 7;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.text(
      `Del: ${formatFechaDMY(fechaInicio)}  al  ${formatFechaDMY(fechaFin)}`,
      pageW / 2,
      y,
      { align: 'center' }
    );
    y += 4;
    const cajaTxt =
      cajaDescripcion ||
      `Cajas: ${cajaInicio ?? '—'}  a  ${cajaFin ?? '—'}`;
    const turnoTxt =
      turnoDescripcion ||
      `Turnos: ${turnoInicio ?? '—'}  a  ${turnoFin ?? '—'}`;
    doc.text(`${cajaTxt}    ·    ${turnoTxt}`, pageW / 2, y, {
      align: 'center',
    });
    y += 10;
  }

  const meseroRowOrder =
    mayorIngreso ? 'importeDesc' : mayorVenta ? 'cantidadDesc' : 'default';
  const meseroGroupOrder = ordenMeserosFormToGroupOrder(ordenMeseros);
  const tree = buildProductosMeseroTree(rows, {
    meseroRowOrder,
    meseroGroupOrder,
    sinApartado: globalSoloTotalesPorFamilia || noGlobalAsterisco,
  });
  const soloPorFamilia = tree.length === 1 && tree[0]?.key === MESERO_KEY_GENERAL;

  const flatPlanoMesero = noGlobalAsterisco && !globalSoloTotalesPorFamilia;

  if (flatPlanoMesero) {
    const planoList = rows.map(normalizeProductosMeseroRow).filter(Boolean);
    const upperPdf = (s) => String(s ?? '').trim().toUpperCase();
    const headerNavyPlano = [26, 43, 60];
    const bodyPlano = planoList.map((r) => {
      let tipoStr = r.tipoProducto || '';
      if (!tipoStr) tipoStr = r.esBebidas ? 'BEBIDA' : 'ALIMENTO';
      return [
        upperPdf(r.familia),
        r.codigo,
        upperPdf(r.nombre),
        upperPdf(tipoStr),
        fmt(r.cantidad),
        fmtMoney(r.importe),
        fmtMoney(r.costo),
        fmtMoney(r.totalCosto),
        fmtMoney(r.precio),
        fmtMoney(r.utilidad),
        r.meseroId != null && r.meseroId !== '' ? String(r.meseroId) : '—',
        upperPdf(r.meseroNombre),
      ];
    });

    const tgPlano = planoList.reduce(
      (acc, r) => ({
        cantidad: acc.cantidad + Number(r.cantidad ?? 0),
        importe: acc.importe + Number(r.importe ?? 0),
        totalCosto: acc.totalCosto + Number(r.totalCosto ?? 0),
        utilidad: acc.utilidad + Number(r.utilidad ?? 0),
      }),
      { cantidad: 0, importe: 0, totalCosto: 0, utilidad: 0 }
    );

    const totSt = (extra = {}) => ({
      fillColor: [29, 78, 216],
      textColor: [248, 250, 252],
      fontStyle: 'bold',
      ...extra,
    });

    bodyPlano.push([
      {
        content: 'TOTAL GENERAL',
        colSpan: 4,
        styles: totSt({ halign: 'left' }),
      },
      { content: fmt(tgPlano.cantidad), styles: totSt({ halign: 'right' }) },
      { content: fmtMoney(tgPlano.importe), styles: totSt({ halign: 'right' }) },
      { content: '', styles: totSt({}) },
      { content: fmtMoney(tgPlano.totalCosto), styles: totSt({ halign: 'right' }) },
      { content: '', styles: totSt({}) },
      { content: fmtMoney(tgPlano.utilidad), styles: totSt({ halign: 'right' }) },
      { content: '', colSpan: 2, styles: totSt({}) },
    ]);

    if (y > doc.internal.pageSize.getHeight() - 35) {
      doc.addPage();
      y = margin;
    }
    autoTable(doc, {
      startY: y,
      head: [
        [
          'familia',
          'codigo',
          'nombreProducto',
          'tipoProducto',
          'cantidad',
          'importe',
          'costo',
          'costoTotal',
          'precio',
          'utilidad',
          'mesero',
          'meseroNombre',
        ],
      ],
      body: bodyPlano,
      theme: 'grid',
      headStyles: {
        fillColor: headerNavyPlano,
        textColor: [248, 250, 252],
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { cellWidth: 26, halign: 'left' },
        1: { cellWidth: 18, halign: 'left' },
        2: { cellWidth: 38, halign: 'left' },
        3: { cellWidth: 22, halign: 'left' },
        4: { halign: 'right', cellWidth: 16 },
        5: { halign: 'right', cellWidth: 22 },
        6: { halign: 'right', cellWidth: 20 },
        7: { halign: 'right', cellWidth: 22 },
        8: { halign: 'right', cellWidth: 20 },
        9: { halign: 'right', cellWidth: 22 },
        10: { halign: 'right', cellWidth: 14 },
        11: { cellWidth: 28, halign: 'left' },
      },
      styles: {
        fontSize: 6.5,
        cellPadding: 0.9,
        textColor: [15, 23, 42],
        fillColor: [255, 255, 255],
      },
      alternateRowStyles: { fillColor: [255, 255, 255] },
      margin: { left: margin + 3, right: margin },
    });
    y = doc.lastAutoTable.finalY + 8;
  } else {
  for (const mesero of tree) {
    if (globalSoloTotalesPorFamilia) {
      /** Vista global: mismo criterio visual que Excel — barra MESERO navy, tabla grid, cabecera navy, totales en tabla. */
      const headerNavyGlobal = [26, 43, 60];
      const navySoftMesero = [51, 65, 85];

      if (y > doc.internal.pageSize.getHeight() - 55) {
        doc.addPage();
        y = margin;
      }

      if (!soloPorFamilia) {
        const mesBarH = 6.8;
        const mesBarW = pageW - margin * 2;
        doc.setFillColor(...headerNavyGlobal);
        doc.rect(margin, y, mesBarW, mesBarH, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(248, 250, 252);
        doc.text(`MESERO: ${String(mesero.label || '').toUpperCase()}`, margin + 3, y + 4.7);
        doc.setTextColor(0);
        y += mesBarH + 2;
      }

      const familiasGlobal = sortMergedFamiliasGlobales(
        mergeFamiliasGlobales(mesero),
        meseroRowOrder
      );
      const mt = mesero.meseroTotals || {};
      const totalMeseroLabel = soloPorFamilia ? 'TOTAL REPORTE' : 'TOTAL MESERO';

      const softTot = (content, alignRight = false) => ({
        content,
        styles: {
          fillColor: navySoftMesero,
          textColor: [248, 250, 252],
          fontStyle: 'bold',
          halign: alignRight ? 'right' : 'left',
          cellPadding: pdfGlobalCellPad,
        },
      });

      const bodyGlobal = [
        ...familiasGlobal.map((fam) => [
          String(fam.familia ?? ''),
          fmt(fam.totals.cantidad),
          fmtMoney(fam.totals.totalCosto),
          fmtMoney(fam.totals.importe),
          fmtMoney(fam.totals.utilidad),
        ]),
        [
          softTot(totalMeseroLabel, false),
          softTot(fmt(mt.cantidad), true),
          softTot(fmtMoney(mt.totalCosto), true),
          softTot(fmtMoney(mt.importe), true),
          softTot(fmtMoney(mt.utilidad), true),
        ],
      ];

      autoTable(doc, {
        startY: y,
        head: [['Familia', 'Cant.', 'T.Costo', 'Importe', 'Utilidad']],
        body: bodyGlobal,
        theme: 'grid',
        headStyles: {
          fillColor: headerNavyGlobal,
          textColor: [248, 250, 252],
          fontStyle: 'bold',
          cellPadding: pdfGlobalCellPad,
        },
        columnStyles: {
          0: {
            cellWidth: meseroGlobalNombreColW,
            halign: 'left',
            valign: 'middle',
            overflow: 'linebreak',
          },
          1: { halign: 'right', cellWidth: 22 },
          2: { halign: 'right', cellWidth: 22 },
          3: { halign: 'right', cellWidth: 24 },
          4: { halign: 'right', cellWidth: 26 },
        },
        styles: {
          fontSize: 7.5,
          cellPadding: pdfGlobalCellPad,
          textColor: [15, 23, 42],
          fillColor: [255, 255, 255],
          lineColor: [229, 231, 235],
          lineWidth: 0.08,
          valign: 'middle',
        },
        alternateRowStyles: { fillColor: [255, 255, 255] },
        tableWidth: globalTableInnerW,
        margin: { left: margin, right: margin },
      });
      y = doc.lastAutoTable.finalY + 10;
    } else {
      /** Detalle ejecutivo: barra MESERO navy + texto APARTADO + tabla única por apartado (familia como fila del cuerpo) */
      const headerNavy = [26, 43, 60];
      const tableLM = margin + 3;

      if (!soloPorFamilia) {
        if (y > doc.internal.pageSize.getHeight() - 40) {
          doc.addPage();
          y = margin;
        }
        const mesBarH = 6.8;
        const mesBarW = pageW - margin * 2;
        doc.setFillColor(...headerNavy);
        doc.rect(margin, y, mesBarW, mesBarH, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(248, 250, 252);
        doc.text(`MESERO: ${String(mesero.label || '').toUpperCase()}`, margin + 3, y + 4.7);
        doc.setTextColor(0);
        y += mesBarH + 2;

        if (mesero.tCheques != null || mesero.tPax != null) {
          const meta = [];
          if (mesero.tCheques != null) meta.push(`T.Cheques: ${fmt(mesero.tCheques, 0)}`);
          if (mesero.tPax != null) meta.push(`T.Pax: ${fmt(mesero.tPax, 0)}`);
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(71, 85, 105);
          doc.text(meta.join('   ·   '), margin + 3, y + 3);
          doc.setTextColor(0);
          y += 5;
        }
      }

      for (let apartadoIdx = 0; apartadoIdx < mesero.apartados.length; apartadoIdx++) {
        const apartado = mesero.apartados[apartadoIdx];
        if (y > doc.internal.pageSize.getHeight() - 52) {
          doc.addPage();
          y = margin;
        }

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(30, 41, 59);
        doc.text(`APARTADO: ${String(apartado.label || '').toUpperCase()}`, margin + 3, y + 4);
        doc.setTextColor(0);
        y += 5;

        const bodyRows = [];
        for (const fam of apartado.familias) {
          const famLabel = String(fam.familia || '').toUpperCase();
          bodyRows.push([
            {
              content: famLabel,
              colSpan: 8,
              styles: {
                fontStyle: 'bold',
                halign: 'left',
                valign: 'middle',
                fillColor: [255, 255, 255],
                textColor: [15, 23, 42],
                cellPadding: { top: 1, bottom: 1, left: 1.5, right: 1.5 },
                lineWidth: { bottom: 0.12 },
                lineColor: [229, 231, 235],
              },
            },
          ]);
          for (const r of fam.rows) {
            bodyRows.push([
              r.codigo,
              r.nombre,
              fmt(r.cantidad),
              fmtMoney(r.costo),
              fmtMoney(r.totalCosto),
              fmtMoney(r.precio),
              fmtMoney(r.importe),
              fmtMoney(r.utilidad),
            ]);
          }
          bodyRows.push([
            {
              content: 'Total',
              colSpan: 2,
              styles: {
                fontStyle: 'bold',
                halign: 'right',
                fillColor: [241, 245, 249],
              },
            },
            {
              content: fmt(fam.totals.cantidad),
              styles: { halign: 'right', fontStyle: 'bold', fillColor: [241, 245, 249] },
            },
            { content: '', styles: { fillColor: [241, 245, 249] } },
            {
              content: fmtMoney(fam.totals.totalCosto),
              styles: { halign: 'right', fontStyle: 'bold', fillColor: [241, 245, 249] },
            },
            { content: '', styles: { fillColor: [241, 245, 249] } },
            {
              content: fmtMoney(fam.totals.importe),
              styles: { halign: 'right', fontStyle: 'bold', fillColor: [241, 245, 249] },
            },
            {
              content: fmtMoney(fam.totals.utilidad),
              styles: { halign: 'right', fontStyle: 'bold', fillColor: [241, 245, 249] },
            },
          ]);
        }

        autoTable(doc, {
          startY: y,
          head: [['Código', 'Nombre', 'Cant.', 'Costo', 'T.Costo', 'Precio', 'Importe', 'Utilidad']],
          body: bodyRows,
          theme: 'grid',
          didParseCell: (data) => {
            if (data.section !== 'body') return;
            const cs = data.cell.colSpan;
            if (cs != null && cs >= 8) {
              data.cell.styles.halign = 'left';
            }
          },
          headStyles: {
            fillColor: headerNavy,
            textColor: [248, 250, 252],
            fontStyle: 'bold',
          },
          columnStyles: {
            0: { cellWidth: 22, halign: 'left' },
            1: { cellWidth: meseroFamiliaNombreColW, halign: 'left' },
            2: { halign: 'right', cellWidth: 18 },
            3: { halign: 'right', cellWidth: 24 },
            4: { halign: 'right', cellWidth: 24 },
            5: { halign: 'right', cellWidth: 24 },
            6: { halign: 'right', cellWidth: 26 },
            7: { halign: 'right', cellWidth: 26 },
          },
          styles: {
            fontSize: 7.5,
            cellPadding: 1,
            textColor: [15, 23, 42],
            lineColor: [229, 231, 235],
            lineWidth: 0.08,
          },
          alternateRowStyles: { fillColor: [252, 252, 253] },
          margin: { left: tableLM, right: margin },
        });
        y = doc.lastAutoTable.finalY + 3;

        const secTotals = sumApartadoTotalsFromFamilias(apartado.familias);
        y = drawPdfSectionTotalBar(doc, pageW, margin, y, apartado.label, secTotals);
      }
    }

    if (!globalSoloTotalesPorFamilia) {
      if (y > doc.internal.pageSize.getHeight() - 25) {
        doc.addPage();
        y = margin;
      }
      y = drawPdfMeseroSummaryCard(
        doc,
        pageW,
        margin,
        y,
        soloPorFamilia,
        mesero.label,
        mesero.meseroTotals
      );
    }
  }

    if (globalSoloTotalesPorFamilia && tree.length > 0) {
      const tgAll = tree.reduce(
        (acc, m) => ({
          cantidad: acc.cantidad + Number(m?.meseroTotals?.cantidad ?? 0),
          totalCosto: acc.totalCosto + Number(m?.meseroTotals?.totalCosto ?? 0),
          importe: acc.importe + Number(m?.meseroTotals?.importe ?? 0),
          utilidad: acc.utilidad + Number(m?.meseroTotals?.utilidad ?? 0),
        }),
        { cantidad: 0, totalCosto: 0, importe: 0, utilidad: 0 }
      );

      const totGenBlue = [29, 78, 216];
      const genCell = (content, alignRight = false) => ({
        content,
        styles: {
          fillColor: totGenBlue,
          textColor: [248, 250, 252],
          fontStyle: 'bold',
          halign: alignRight ? 'right' : 'left',
          cellPadding: pdfGlobalCellPad,
        },
      });

      if (y > doc.internal.pageSize.getHeight() - 28) {
        doc.addPage();
        y = margin;
      }

      autoTable(doc, {
        startY: y,
        body: [
          [
            genCell('TOTAL GENERAL', false),
            genCell(fmt(tgAll.cantidad), true),
            genCell(fmtMoney(tgAll.totalCosto), true),
            genCell(fmtMoney(tgAll.importe), true),
            genCell(fmtMoney(tgAll.utilidad), true),
          ],
        ],
        theme: 'grid',
        columnStyles: {
          0: {
            cellWidth: meseroGlobalNombreColW,
            halign: 'left',
            valign: 'middle',
            overflow: 'linebreak',
          },
          1: { halign: 'right', cellWidth: 22 },
          2: { halign: 'right', cellWidth: 22 },
          3: { halign: 'right', cellWidth: 24 },
          4: { halign: 'right', cellWidth: 26 },
        },
        styles: {
          fontSize: 7.5,
          cellPadding: pdfGlobalCellPad,
          lineColor: [229, 231, 235],
          lineWidth: 0.08,
          valign: 'middle',
        },
        tableWidth: globalTableInnerW,
        margin: { left: margin, right: margin },
      });
      y = doc.lastAutoTable.finalY + 10;
    }
  }

  if (
    noGlobalAsterisco &&
    !globalSoloTotalesPorFamilia &&
    tree.length > 1 &&
    !(tree.length === 1 && tree[0]?.key === MESERO_KEY_GENERAL)
  ) {
    const tg = tree.reduce(
      (acc, m) => ({
        cantidad: acc.cantidad + (m.meseroTotals?.cantidad || 0),
        totalCosto: acc.totalCosto + (m.meseroTotals?.totalCosto || 0),
        importe: acc.importe + (m.meseroTotals?.importe || 0),
        utilidad: acc.utilidad + (m.meseroTotals?.utilidad || 0),
      }),
      { cantidad: 0, totalCosto: 0, importe: 0, utilidad: 0 }
    );
    if (y > doc.internal.pageSize.getHeight() - 28) {
      doc.addPage();
      y = margin;
    }
    y = drawPdfMeseroSummaryCard(doc, pageW, margin, y, false, '', tg, {
      title: 'Total general',
      sub: '',
      variant: 'general',
    });
  }

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    addFooter(i, totalPages);
  }

  const sufijoNombre = globalSoloTotalesPorFamilia
    ? 'global-'
    : noGlobalAsterisco
      ? 'plano-'
      : 'ejecutivo-';
  doc.save(
    `reporte-productos-mesero-${sufijoNombre}${fechaInicio || 'inicio'}-${fechaFin || 'fin'}.pdf`
  );
}
